import { useRef, useCallback, useEffect, useState } from 'react';
import {
  useLazyGetCaseStatusQuery,
  useLazyGetCaseQuery,
  useLazyGetCaseOverviewQuery,
  useLazyGetCaseDocumentsQuery
} from '@/api';
import { selectCaseDocumentsPagination, useAppSelector } from '@/store';

const LONG_POLLING_CASE_STATUSES_DELAY = 1 * 30 * 1000; // 30 seconds
export const useCaseStatusPolling = (caseId?: string | null, shouldPollOverviewAndDocuments = false) => {
  const [triggerGetCaseStatus] = useLazyGetCaseStatusQuery();
  const [triggerGetCase] = useLazyGetCaseQuery();
  const [triggerGetCaseOverview] = useLazyGetCaseOverviewQuery();
  const [triggerGetCaseDocuments] = useLazyGetCaseDocumentsQuery();
  const caseDocumentsPagination = useAppSelector(selectCaseDocumentsPagination);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(() => {
    if (!caseId) return;

    // Stop any existing polling first
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    setIsPolling(true);

    // Fetch immediately
    const fetchData = () => {
      triggerGetCaseStatus({ caseId });
      triggerGetCase({ caseId });

      // Only poll overview and documents if flag is true (for CLONING status)
      if (shouldPollOverviewAndDocuments) {
        triggerGetCaseOverview({ caseId });
        triggerGetCaseDocuments({
          caseId: caseId,
          ...caseDocumentsPagination
        });
      }
    };

    fetchData();

    // Set up interval
    intervalRef.current = setInterval(fetchData, LONG_POLLING_CASE_STATUSES_DELAY);
  }, [
    caseId,
    shouldPollOverviewAndDocuments,
    triggerGetCaseStatus,
    triggerGetCase,
    triggerGetCaseOverview,
    triggerGetCaseDocuments,
    caseDocumentsPagination
  ]);

  // Stop polling on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { startPolling, stopPolling, isPolling };
};
